<script setup lang="ts">
import useVuelidate from '@vuelidate/core';
import { useI18n } from 'vue-i18n';
import { pickupOptions } from '../../../types/Address';
import { useUserStore } from '../../../stores/user';
import { useOrderStore } from '../../../stores/orderForm';
import { vRequired, vRequiredIf } from '../../../utils/vuelidateValidators';
import { getDeliveryDate } from '../../../utils/date';
import { useHolidaysStore } from '../../../stores/holiday';

const { t } = useI18n();
const { order, disabledPreview, adminView } = useOrderStore();

const userStore = useUserStore();

const deliveryOpts = pickupOptions.map(value => ({ label: t(`addresses.delivery.${value}`), value }));

const rules = computed(() => ({
  delivery_time: { required: vRequired() },
  address: {
    name: { required: vRequiredIf(order.pickup !== 'fromShop') },
    phone: { required: vRequiredIf(order.pickup !== 'fromShop') },
    city: { required: vRequiredIf(order.pickup !== 'fromShop') },
    address: { required: vRequiredIf(order.pickup === 'address' || order.pickup === 'ourCourier') },
    courier: { required: vRequiredIf(order.pickup === 'address' || order.pickup === 'office') },
    office: { required: vRequiredIf(order.pickup === 'office') },
  },
}));

const v$ = useVuelidate(rules, order);

// Addresses and pickup
watch(() => order.pickup, (newVal) => {
  switch (newVal) {
    case 'address':
      order.address = { ...userStore.profile.addresses.courierAddress };
      break;
    case 'office':
      order.address = { ...userStore.profile.addresses.courierOffice };
      break;
    case 'ourCourier':
      order.address = { ...userStore.profile.addresses.ourCourier };
      if (order.delivery_time.getHours() < 13) {
        const newD = new Date(order.delivery_time);
        newD.setHours(13, 0, 0, 0);
        order.delivery_time = newD;
      }
      break;
    default:
      order.address = null;
      break;
  }
});

function onOurCourierChange(value: string | undefined, field: string) {
  (order.address as any)[field] = value;
};
function onAddressCourierChange(value: string | undefined, field: string) {
  (order.address as any)[field] = value;
};
function onOfficeCourierChange(value: string | undefined, field: string) {
  (order.address as any)[field] = value;
};

// Dates
const { holidays } = useHolidaysStore();
const minDate = ref(adminView ? undefined : getDeliveryDate({ date: new Date(), holidays }));
const wasDateModified = ref(false);
// const minDate = adminView ? undefined : getDeliveryDate({ date: new Date(), holidays, hasCourier: order.pickup === pickupOptions['0'] });

watch(() => order.pickup, (newPickup) => {
  if (adminView)
    return;

  minDate.value = getDeliveryDate({ date: new Date(), holidays, hasCourier: newPickup === 'office' || newPickup === 'address' });
  minDate.value.setHours(order.delivery_time.getHours());
  minDate.value.setMinutes(order.delivery_time.getMinutes());

  // Reset the delivery_time when the user's choice is not valid anymore OR when he has not selected anything and we want to use the system defaults
  if (wasDateModified.value) {
    if (minDate.value.getTime() > order.delivery_time.getTime()) {
      order.delivery_time = new Date(minDate.value);
    }
  }
  else {
    order.delivery_time = new Date(minDate.value);
  }
});

function onDateSelect(date: undefined | string | string[] | Date | Date[]) {
  // Track if user has changed the date
  wasDateModified.value = true;
  if (!(date instanceof Date))
    return;
  const newDate = new Date(date);
  newDate.setHours(order.delivery_time.getHours(), order.delivery_time.getMinutes(), 0, 0);
  order.delivery_time = newDate;
}

function onTimeChange(date: undefined | string | string[] | Date | Date[]) {
  if (!(date instanceof Date))
    return;

  const newDate = new Date(date);
  if (order.pickup === 'ourCourier' && newDate.getHours() < 13) {
    newDate.setHours(13, 0, 0, 0);
  }
  else if (order.pickup !== 'ourCourier' && newDate.getHours() < 9) {
    newDate.setHours(9, 0, 0, 0);
  }
  order.delivery_time = newDate;
}
</script>

<template>
  <Fieldset class="mb-6" :pt="{ legend: 'bg-gray-200 border border-gray-300 hover:bg-gray-300' }">
    <template #legend>
      <div class="align-items-center flex gap-2 px-2">
        <span class="font-bold">Доставка</span>
        <i class="pi pi-box !leading-[unset]" />
      </div>
    </template>

    <div class="mb-6 pd-form-cols-2">
      <FormItem
        field="date"
        :label="$t('orderForm.date.label')"
        :v$="v$"
        required
      >
        <Calendar
          id="date"
          :model-value="order.delivery_time"
          :min-date="minDate"
          date-format="dd.mm.yy"
          class="w-full"
          :disabled="disabledPreview"
          @update:model-value="onDateSelect"
        />
      </FormItem>

      <FormItem field="time" :label="$t('orderForm.time.label')">
        <Calendar
          id="time"
          :model-value="order.delivery_time"
          time-only
          class="w-full"
          :disabled="disabledPreview"
          @update:model-value="onTimeChange"
        />
      </FormItem>
    </div>

    <div class="mb-6 pd-form-cols-2">
      <FormItem
        field="pickup"
        label="Начин на доставка"
        :v$="v$"
        required
      >
        <Dropdown
          v-model="order.pickup"
          :options="deliveryOpts"
          :placeholder="$t('orderForm.color.placeholder')"
          option-label="label"
          option-value="value"
          :disabled="disabledPreview"
          class="w-full"
        />
      </FormItem>
    </div>

    <OurCourierFormModule
      v-if="order.pickup === 'ourCourier' && !!order.address"
      :data="order.address as any"
      :v$="v$.address as any"
      :disabled="disabledPreview"
      @change="onOurCourierChange"
    />
    <CourierAddressFormModule
      v-if="order.pickup === 'address' && !!order.address"
      :data="order.address as any"
      :v$="v$.address as any"
      :disabled="disabledPreview"
      @change="onAddressCourierChange"
    />
    <CourierOfficeFormModule
      v-if="order.pickup === 'office' && !!order.address"
      :data="order.address as any"
      :v$="v$.address as any"
      :disabled="disabledPreview"
      @change="onOfficeCourierChange"
    />
  </Fieldset>
</template>
